import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row } from 'reactstrap';
import { toast } from 'react-toastify';
import { getAuthToken } from '../../../../../../../auth/tokenvalidation';
import ClauseCard from '../../../../../../../shared/offer-domain/clause';
import {
  SectionHeader,
  SectionAlert,
  SectionCard,
} from '../../../../../../../shared/offer-domain/ui';
import useClarificationClauseResponses from '../../../../../../../shared/offer-domain/respond/useClarificationClauseResponses';
import useSubmittedBidder from '../../../../../hooks/useSubmittedBidder';
import {
  getOwnClauseResponse,
  getLastOwnEditableResponse,
  getVisibleProcurementClauses,
  hasClauseResponse,
  toClauseResponseList,
  needsIcReplyOnEvaluatorThread,
  needsTeAgreeOnLastCounter,
  hasTeClarificationRespondReady,
  createLockRespondHandler,
} from '../../../../../../../shared/offer-domain/respond';

const EVALUATOR_FORM_MAIN = {
  statusOfferingWordingFlag: false,
};

const ClauseDetailSection = ({
  procurement,
  procurementId,
  offerId,
  procurementMitraId: procurementMitraIdProp = null,
  isEvaluatorView = true,
  isRespondClosed = false,
  highlightArmed = false,
  onPendingRespondIds,
}) => {
  const token = getAuthToken();
  const { items } = useSubmittedBidder(procurementId, {
    enabled: !procurementMitraIdProp,
  });
  const [draftNotes, setDraftNotes] = useState({});
  const [expandedClauses, setExpandedClauses] = useState({});

  const bidder = useMemo(() => {
    if (procurementMitraIdProp) {
      return { id: procurementMitraIdProp, idOffer: offerId };
    }
    return (
      items.find(
        item =>
          String(item.idOffer) === String(offerId) ||
          String(item.id) === String(offerId)
      ) ?? null
    );
  }, [items, offerId, procurementMitraIdProp]);

  const visibleClauses = useMemo(
    () => getVisibleProcurementClauses(procurement),
    [procurement]
  );

  const clauseDetailIds = useMemo(
    () => visibleClauses.map(clause => clause.id).filter(Boolean),
    [visibleClauses]
  );

  const {
    clauseResponses,
    isLoading: isClauseResponsesLoading,
    isSaving: isClauseResponsesSaving,
    saveResponse: saveClauseResponse,
    deleteResponse: deleteClauseResponse,
    lockResponse,
  } = useClarificationClauseResponses(
    token,
    bidder?.id,
    clauseDetailIds,
    isEvaluatorView,
    procurementId
  );

  const handleLockResponse = createLockRespondHandler(lockResponse);

  useEffect(() => {
    setDraftNotes({});
    setExpandedClauses({});
  }, [bidder?.id, offerId]);

  useEffect(() => {
    if (isClauseResponsesLoading) return;

    const expanded = {};
    const drafts = {};

    Object.entries(clauseResponses).forEach(([clauseId, responses]) => {
      const lastEditable = getLastOwnEditableResponse(responses, {
        isEvaluator: isEvaluatorView,
      });
      if (lastEditable && hasClauseResponse(lastEditable?.respondNotes)) {
        expanded[clauseId] = true;
        drafts[clauseId] = lastEditable.respondNotes;
      } else if (
        toClauseResponseList(responses).some(item =>
          hasClauseResponse(item?.respondNotes)
        )
      ) {
        expanded[clauseId] = true;
      }
    });

    setExpandedClauses(expanded);
    setDraftNotes(drafts);
  }, [clauseResponses, isClauseResponsesLoading, isEvaluatorView, bidder?.id]);

  const isPendingThread = useCallback(
    list =>
      isEvaluatorView
        ? needsTeAgreeOnLastCounter(list)
        : needsIcReplyOnEvaluatorThread(list),
    [isEvaluatorView]
  );

  useEffect(() => {
    if (!highlightArmed) return;
    setExpandedClauses(prev => {
      const next = { ...prev };
      let changed = false;
      Object.entries(clauseResponses).forEach(([clauseId, list]) => {
        if (isPendingThread(list) && !next[clauseId]) {
          next[clauseId] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [highlightArmed, clauseResponses, isPendingThread]);

  useEffect(() => {
    if (isClauseResponsesLoading) return;
    const ids = Object.entries(clauseResponses)
      .filter(([, list]) => isPendingThread(list))
      .map(([clauseId]) => `clause-${clauseId}`);
    const hasClarificationRespond = Object.values(clauseResponses).some(
      hasTeClarificationRespondReady
    );
    onPendingRespondIds?.(ids, { hasClarificationRespond });
  }, [
    clauseResponses,
    onPendingRespondIds,
    isClauseResponsesLoading,
    isPendingThread,
  ]);

  const handleDraftChange = (clauseId, value) => {
    setDraftNotes(prev => ({ ...prev, [clauseId]: value }));
  };

  const toggleClauseExpand = clauseId => {
    setExpandedClauses(prev => {
      const list = toClauseResponseList(clauseResponses[clauseId]);
      const defaultExpanded = list.some(item =>
        hasClauseResponse(item?.respondNotes)
      );
      return {
        ...prev,
        [clauseId]: !(prev[clauseId] ?? defaultExpanded),
      };
    });
  };

  const handleClauseSave = async clauseId => {
    const respondNotes = String(draftNotes[clauseId] ?? '').trim();
    if (!respondNotes) {
      toast.error('Respond notes cannot be empty');
      return;
    }

    try {
      const saved = await saveClauseResponse(clauseId, respondNotes);
      if (!saved) {
        toast.error('Failed to save response');
        return;
      }
      const syncedNotes = saved?.respondNotes ?? respondNotes;
      setExpandedClauses(prev => ({ ...prev, [clauseId]: true }));
      setDraftNotes(prev => ({ ...prev, [clauseId]: syncedNotes }));
      toast.success('Response saved successfully');
    } catch {
      toast.error('Failed to save response');
    }
  };

  const handleClauseCancel = clauseId => {
    const saved = isEvaluatorView
      ? (getOwnClauseResponse(clauseResponses[clauseId], {
          isEvaluator: true,
        })?.respondNotes ?? '')
      : '';
    setDraftNotes(prev => ({ ...prev, [clauseId]: saved }));
    if (!hasClauseResponse(saved)) {
      setExpandedClauses(prev => ({ ...prev, [clauseId]: false }));
    }
  };

  const handleClauseClear = async clauseId => {
    try {
      await deleteClauseResponse(clauseId);
      setDraftNotes(prev => ({ ...prev, [clauseId]: '' }));
      const ownResponse = getOwnClauseResponse(clauseResponses[clauseId], {
        isEvaluator: isEvaluatorView,
      });
      const hasRemaining = toClauseResponseList(clauseResponses[clauseId])
        .filter(item => item?.id !== ownResponse?.id)
        .some(item => hasClauseResponse(item?.respondNotes));
      setExpandedClauses(prev => ({ ...prev, [clauseId]: hasRemaining }));
      toast.success('Response removed successfully');
    } catch {
      toast.error('Failed to remove response');
    }
  };

  const handleClauseSecondaryAction = (clauseId, hasSaved) => {
    if (hasSaved) {
      handleClauseClear(clauseId);
    } else {
      handleClauseCancel(clauseId);
    }
  };

  return (
    <SectionCard>
      <SectionHeader
        title='Clause Details'
        subtitle='Review procurement clauses and provide clarification responses'
        countLabel={`${visibleClauses.length} clauses`}
      />

      <SectionAlert title='Specification Compliance Notice'>
        <div className='text-muted small'>
          Any information that does not align with Pertamina&apos;s established
          standards may be subject to further evaluation and acceptance at
          Pertamina&apos;s discretion.
        </div>
      </SectionAlert>

      {visibleClauses.length > 0 ? (
        <Row>
          {visibleClauses.map((clause, index) => (
            <ClauseCard
              key={clause.id}
              index={index + 1}
              clause={clause}
              clauseResponses={clauseResponses}
              draftNotes={draftNotes}
              expandedClauses={expandedClauses}
              formMain={EVALUATOR_FORM_MAIN}
              isClauseResponsesSaving={isClauseResponsesSaving}
              isClauseResponsesLoading={isClauseResponsesLoading}
              isEvaluatorView={isEvaluatorView}
              handleDraftChange={handleDraftChange}
              handleClauseSecondaryAction={handleClauseSecondaryAction}
              handleClauseSave={handleClauseSave}
              toggleClauseExpand={toggleClauseExpand}
              onLockResponse={
                isEvaluatorView && !isRespondClosed
                  ? handleLockResponse
                  : undefined
              }
              isRespondClosed={isRespondClosed}
              anchorId={`ic-respond-clause-${clause.id}`}
              highlightPending={
                highlightArmed && isPendingThread(clauseResponses[clause.id])
              }
            />
          ))}
        </Row>
      ) : (
        <div className='text-center py-4 text-muted border rounded-3 bg-light-subtle'>
          No clause data available.
        </div>
      )}
    </SectionCard>
  );
};

export default ClauseDetailSection;
